import React from 'react';
import * as Dialog from '@radix-ui/react-dialog';
import { X } from 'lucide-react';

export interface ConfirmDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  title: string;
  description: string;
  confirmText?: string;
  cancelText?: string;
  variant?: 'default' | 'destructive';
  onConfirm: () => void;
  onCancel?: () => void;
}

export const ConfirmDialog: React.FC<ConfirmDialogProps> = ({
  open,
  onOpenChange,
  title,
  description,
  confirmText = 'Confirm',
  cancelText = 'Cancel',
  variant = 'default',
  onConfirm,
  onCancel,
}) => {
  const handleConfirm = () => {
    onConfirm();
    onOpenChange(false);
  };

  const handleCancel = () => {
    onCancel?.();
    onOpenChange(false);
  };

  const getConfirmButtonClasses = () => {
    // MONOCHROME, including destructive (owner 2026-08-23). A red button and a warning triangle were the only
    // colour in this dialog, and they made every confirm shout the same way regardless of what was at stake.
    // The WORDS carry the weight instead — the copy names exactly what is destroyed — which is both quieter
    // and more informative than a red rectangle. Semantic colour stays reserved for actual STATE
    // (success/failure), never for asking a question.
    // Monochrome-first: the primary confirm action is near-black (white on dark), NOT a brand blue.
    return 'bg-secondary-900 hover:bg-black focus:ring-secondary-500 text-white dark:bg-white dark:text-secondary-900 dark:hover:bg-secondary-100';
  };

  return (
    <Dialog.Root open={open} onOpenChange={onOpenChange}>
      <Dialog.Portal>
        <Dialog.Overlay className="fixed inset-0 bg-black/50 z-[60] data-[state=open]:animate-overlayShow" />
        <Dialog.Content className="fixed top-1/2 left-1/2 max-h-[85dvh] w-[90vw] max-w-md -translate-x-1/2 -translate-y-1/2 rounded-lg bg-white dark:bg-secondary-800 p-6 shadow-xl z-[60] data-[state=open]:animate-contentShow focus:outline-none">
          <div className="flex items-start gap-4">
            <div className="flex-1">
              <Dialog.Title className="text-lg font-semibold text-secondary-900 dark:text-white mb-2">
                {title}
              </Dialog.Title>
              <Dialog.Description className="text-sm text-secondary-600 dark:text-secondary-300 mb-6">
                {description}
              </Dialog.Description>

              <div className="flex justify-end gap-3">
                <button
                  onClick={handleCancel}
                  className="px-4 py-2 text-sm font-medium text-secondary-700 dark:text-secondary-300 bg-secondary-100 dark:bg-secondary-700 hover:bg-secondary-200 dark:hover:bg-secondary-600 rounded-lg transition-colors focus:outline-none focus:ring-2 focus:ring-secondary-500"
                >
                  {cancelText}
                </button>
                <button
                  onClick={handleConfirm}
                  className={`px-4 py-2 text-sm font-medium rounded-lg transition-colors focus:outline-none focus:ring-2 ${getConfirmButtonClasses()}`}
                >
                  {confirmText}
                </button>
              </div>
            </div>

            <Dialog.Close asChild>
              <button
                className="absolute top-4 right-4 text-secondary-400 hover:text-secondary-600 dark:text-secondary-500 dark:hover:text-secondary-300"
                aria-label="Close"
              >
                <X className="h-4 w-4" />
              </button>
            </Dialog.Close>
          </div>
        </Dialog.Content>
      </Dialog.Portal>
    </Dialog.Root>
  );
};